'use client'

import { CalendarDaysIcon, CheckBadgeIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { format, isValid } from 'date-fns'
import { motion } from 'framer-motion'
import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import QuizAddressAutocomplete, {
  type QuizAddressPrediction,
} from '@/features/quiz/inputs/QuizAddressAutocomplete'
import DatePickerCalendar, {
  type DatePickerCalendarValue,
} from '@/shared/components/DatePickerCalendar'
import { WebConfig } from '@/lib/config'
import { formatDate, parseDate } from '@/lib/utils'
import { AU_STATES } from '@/shared/forms/components/PersonalInformationFormFields'
import {
  QuizFieldError,
  prescriptionQuestionItemMotion,
  prescriptionQuestionListMotion,
} from '@/features/prescriptions/request/PrescriptionQuizShell'

const ADDRESS_FIELD_NAMES = ['formattedAddress', 'suburb', 'state', 'postcode']

export default function PrescriptionPersonalDetailsFields({
  disabled,
  verifiedFields,
  emailDisabled = false,
}: {
  disabled: boolean
  verifiedFields: Set<string>
  emailDisabled?: boolean
}) {
  const { clearErrors, setValue, trigger, watch } = useFormContext()
  const formattedAddress = watch('formattedAddress') ?? ''

  async function handleAddressSelect(prediction: QuizAddressPrediction) {
    const fallback = parsePredictionAddress(prediction)
    clearErrors(ADDRESS_FIELD_NAMES)
    setAddressValues(fallback, false)

    const place = prediction.toPlace?.()
    let resolvedAddress = fallback

    if (place) {
      try {
        await place.fetchFields({
          fields: ['formattedAddress', 'addressComponents'],
        })
        resolvedAddress = parsePlaceAddress(place, prediction)
      } catch {
        // The prediction remains available for manual completion.
      }
    }

    setAddressValues(resolvedAddress, true)
    await trigger(ADDRESS_FIELD_NAMES)
  }

  function setAddressValues(
    values: {
      formattedAddress: string
      suburb: string
      state: string
      postcode: string
    },
    shouldTouch: boolean,
  ) {
    for (const [key, value] of Object.entries(values)) {
      setValue(key, value, {
        shouldDirty: true,
        shouldTouch,
        shouldValidate: false,
      })
    }
  }

  return (
    <motion.div
      animate="animate"
      className="grid grid-cols-2 gap-x-4 gap-y-[18px] sm:gap-y-5"
      initial="initial"
      variants={prescriptionQuestionListMotion}
    >
      <QuizInput name="firstName" label="First name" disabled={disabled} />
      <QuizInput name="lastName" label="Last name" disabled={disabled} />
      <QuizSelect
        className="col-span-2"
        disabled={disabled}
        label="Gender"
        name="gender"
        options={[
          { id: 'M', label: 'Male' },
          { id: 'F', label: 'Female' },
        ]}
        placeholder="Select gender"
      />
      <QuizInput
        autoComplete="tel"
        disabled={disabled}
        inputMode="numeric"
        label="Mobile"
        name="mobile"
        placeholder="0400000000"
        type="tel"
        verified={verifiedFields.has('mobile')}
      />
      <QuizInput
        autoComplete="email"
        disabled={disabled || emailDisabled}
        label="Email"
        name="email"
        placeholder="you@email.com"
        type="email"
        verified={verifiedFields.has('email')}
      />
      <DobField disabled={disabled} />

      <motion.div
        className="col-span-2"
        variants={prescriptionQuestionItemMotion}
      >
        <QuizAddressAutocomplete
          className="[&_label+div>div:first-child]:h-[52px] [&_label+div>div:first-child]:rounded-[14px] [&_label+div>div:first-child]:border-0 [&_label+div>div:first-child]:bg-[#F4F4F6] [&_label+div>div:first-child]:px-4 [&_label+div>div:first-child]:shadow-none [&_label]:mb-2 [&_label]:text-[13.5px] [&_label]:leading-[17px] [&_label]:text-[#0B0B0F]"
          disabled={disabled}
          id="formattedAddress"
          label="Address"
          name="formattedAddress"
          onManualEntry={(value) =>
            setValue('formattedAddress', value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            })
          }
          onSelect={handleAddressSelect}
          onValueChange={(value) =>
            setValue('formattedAddress', value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            })
          }
          placeholder="Start typing your address…"
          value={formattedAddress}
        />
        <ControlledError name="formattedAddress" />
      </motion.div>

      <QuizInput
        className="col-span-2 sm:col-span-1"
        autoComplete="address-level2"
        disabled={disabled}
        label="Suburb"
        name="suburb"
      />
      <div className="col-span-2 grid grid-cols-2 gap-4 sm:col-span-1">
        <QuizSelect
          disabled={disabled}
          label="State"
          name="state"
          options={AU_STATES.map((state) => ({ id: state, label: state }))}
          placeholder="Pick"
        />
        <QuizInput
          autoComplete="postal-code"
          disabled={disabled}
          inputMode="numeric"
          label="Postcode"
          name="postcode"
        />
      </div>

      {WebConfig.prescriptionConformanceMode && (
        <>
          <QuizInput
            className="col-span-2"
            disabled={disabled}
            label="Concession number"
            name="concessionNumber"
          />
          <QuizInput
            className="col-span-2"
            disabled={disabled}
            label="Entitlement number"
            name="entitlementNumber"
          />
        </>
      )}

      <QuizTerms disabled={disabled} />
    </motion.div>
  )
}

function QuizInput({
  name,
  label,
  className,
  verified,
  ...props
}: {
  name: string
  label: string
  className?: string
  verified?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'name'>) {
  const { field, fieldState, formState } = useController({ name })
  const showError =
    (fieldState.isTouched || formState.isSubmitted) && Boolean(fieldState.error)

  return (
    <motion.div
      className={clsx('col-span-1 min-w-0', className)}
      variants={prescriptionQuestionItemMotion}
    >
      <label
        className="mb-2 block text-[13.5px] font-semibold leading-[17px]"
        htmlFor={name}
      >
        {label}
      </label>
      <div className="relative">
        <input
          {...field}
          {...props}
          className={clsx(
            'h-[52px] w-full rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-4 text-[15px] leading-5 text-[#0B0B0F] outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-[#A0A0A8] focus:border-[#0B0B0F55] focus:shadow-[0_0_0_3px_rgba(11,11,15,0.05)] disabled:cursor-not-allowed disabled:opacity-50',
            showError && 'border-[#C6453C] focus:border-[#C6453C]',
            verified && 'pr-11',
          )}
          aria-invalid={showError}
          aria-describedby={showError ? `${name}-error` : undefined}
          id={name}
          value={field.value ?? ''}
        />
        {verified && (
          <CheckBadgeIcon className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#1F8D77]" />
        )}
      </div>
      {showError && (
        <QuizFieldError id={`${name}-error`}>
          {fieldState.error?.message}
        </QuizFieldError>
      )}
    </motion.div>
  )
}

function QuizSelect({
  name,
  label,
  options,
  placeholder,
  disabled,
  className,
}: {
  name: string
  label: string
  options: Array<{ id: string; label: string }>
  placeholder: string
  disabled: boolean
  className?: string
}) {
  const { field, fieldState, formState } = useController({ name })
  const showError =
    (fieldState.isTouched || formState.isSubmitted) && Boolean(fieldState.error)

  return (
    <motion.div
      className={clsx('min-w-0', className)}
      variants={prescriptionQuestionItemMotion}
    >
      <label
        className="mb-2 block text-[13.5px] font-semibold leading-[17px]"
        htmlFor={name}
      >
        {label}
      </label>
      <select
        {...field}
        className={clsx(
          'h-[52px] w-full appearance-none rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] bg-[linear-gradient(45deg,transparent_50%,#82828B_50%),linear-gradient(135deg,#82828B_50%,transparent_50%)] bg-[size:4px_4px,4px_4px] bg-[position:calc(100%-18px)_23px,calc(100%-14px)_23px] bg-no-repeat px-4 pr-9 text-[15px] leading-5 outline-none transition-colors duration-300 focus:border-[#0B0B0F55] disabled:opacity-50',
          !field.value && 'text-[#A0A0A8]',
          showError && 'border-[#C6453C]',
        )}
        disabled={disabled}
        aria-invalid={showError}
        aria-describedby={showError ? `${name}-error` : undefined}
        id={name}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      {showError && (
        <QuizFieldError id={`${name}-error`}>
          {fieldState.error?.message}
        </QuizFieldError>
      )}
    </motion.div>
  )
}

function DobField({ disabled }: { disabled: boolean }) {
  const { field, fieldState, formState } = useController({ name: 'dob' })
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const parsedDate = field.value ? parseDate(field.value) : null
  const currentValue =
    parsedDate && isValid(parsedDate)
      ? {
          year: parsedDate.getFullYear(),
          month: parsedDate.getMonth() + 1,
          day: parsedDate.getDate(),
        }
      : undefined
  const showError =
    (fieldState.isTouched || formState.isSubmitted) && Boolean(fieldState.error)

  useEffect(() => {
    if (!open) return
    function closeOnOutsideClick(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    return () =>
      document.removeEventListener('pointerdown', closeOnOutsideClick)
  }, [open])

  function selectDate(value: DatePickerCalendarValue) {
    field.onChange(formatDate(new Date(value.year, value.month - 1, value.day)))
    field.onBlur()
    setOpen(false)
  }

  return (
    <motion.div
      className="relative col-span-2"
      ref={rootRef}
      variants={prescriptionQuestionItemMotion}
    >
      <label
        htmlFor="dob"
        className="mb-2 block text-[13.5px] font-semibold leading-[17px]"
      >
        Date of birth
      </label>
      <button
        id="dob"
        ref={field.ref}
        aria-describedby={showError ? 'dob-error' : undefined}
        className={clsx(
          'flex h-[52px] w-full items-center rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-4 text-left text-[15px] leading-5 outline-none transition-colors duration-300 focus:border-[#0B0B0F55] disabled:opacity-50',
          !currentValue && 'text-[#A0A0A8]',
          showError && 'border-[#C6453C]',
        )}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        {currentValue && parsedDate
          ? format(parsedDate, 'd MMMM yyyy')
          : 'Pick a date'}
        <CalendarDaysIcon className="ml-auto h-4 w-4 text-[#82828B]" />
      </button>
      {open && (
        <div className="absolute left-0 top-[82px] z-50 sm:left-auto sm:right-0">
          <DatePickerCalendar
            defaultValue={currentValue}
            endYear={new Date().getFullYear() - 18}
            initialView="year"
            onChange={selectDate}
            startYear={new Date().getFullYear() - 100}
            value={currentValue}
          />
        </div>
      )}
      {showError && (
        <QuizFieldError id="dob-error">
          {fieldState.error?.message}
        </QuizFieldError>
      )}
    </motion.div>
  )
}

function QuizTerms({ disabled }: { disabled: boolean }) {
  const { field, fieldState, formState } = useController({ name: 'acceptTnc' })
  const showError =
    (fieldState.isTouched || formState.isSubmitted) && Boolean(fieldState.error)

  return (
    <motion.div
      className="col-span-2"
      variants={prescriptionQuestionItemMotion}
    >
      <label className="flex cursor-pointer items-start gap-3 text-[12.5px] leading-[18px] text-[#5C5C64] sm:text-[13px]">
        <input
          checked={Boolean(field.value)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#C4C4CA] text-[#0B0B0F] focus:ring-[#0B0B0F]/20"
          disabled={disabled}
          ref={field.ref}
          aria-invalid={showError}
          name={field.name}
          onBlur={field.onBlur}
          onChange={field.onChange}
          type="checkbox"
        />
        <span>
          I accept NextClinic&apos;s terms and conditions and privacy policy.
        </span>
      </label>
      {showError && (
        <QuizFieldError>{fieldState.error?.message}</QuizFieldError>
      )}
    </motion.div>
  )
}

function ControlledError({ name }: { name: string }) {
  const { fieldState, formState } = useController({ name })
  if ((!fieldState.isTouched && !formState.isSubmitted) || !fieldState.error)
    return null
  return <QuizFieldError>{fieldState.error.message}</QuizFieldError>
}

function parsePredictionAddress(prediction: QuizAddressPrediction) {
  const normalizedSecondaryText = prediction.secondaryText
    .replace(/,?\s*Australia$/i, '')
    .replace(/,\s*/g, ' ')
    .trim()
  const match = normalizedSecondaryText.match(
    /^(.*)\s+(ACT|NSW|NT|QLD|SA|TAS|VIC|WA)\s+(\d{4})$/,
  )
  return {
    formattedAddress: prediction.mainText || prediction.description,
    suburb: match?.[1] ?? '',
    state: match?.[2] ?? '',
    postcode: match?.[3] ?? '',
  }
}

function parsePlaceAddress(
  place: {
    formattedAddress?: string | null
    addressComponents?: readonly unknown[] | null
  },
  prediction: QuizAddressPrediction,
) {
  const fallback = parsePredictionAddress(prediction)
  let suburb = fallback.suburb
  let state = fallback.state
  let postcode = fallback.postcode
  let subpremise = ''
  let streetNumber = ''
  let route = ''

  for (const rawComponent of place.addressComponents ?? []) {
    const component = rawComponent as {
      types?: string[]
      shortText?: string
      longText?: string
      short_name?: string
      long_name?: string
    }
    const shortValue = component.shortText ?? component.short_name ?? ''
    const longValue = component.longText ?? component.long_name ?? shortValue
    if (
      component.types?.some((type) =>
        ['locality', 'postal_town', 'sublocality_level_1'].includes(type),
      )
    ) {
      suburb = longValue
    }
    if (component.types?.includes('administrative_area_level_1')) {
      state = shortValue
    }
    if (component.types?.includes('postal_code')) postcode = shortValue
    if (component.types?.includes('subpremise')) subpremise = longValue
    if (component.types?.includes('street_number')) streetNumber = longValue
    if (component.types?.includes('route')) route = longValue
  }

  const streetAddress = [streetNumber, route].filter(Boolean).join(' ')
  const formattedAddress =
    subpremise && streetAddress
      ? `${subpremise}/${streetAddress}`
      : streetAddress || fallback.formattedAddress

  return { formattedAddress, suburb, state, postcode }
}
